/* @license
 * Copyright 2019 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the 'License');
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an 'AS IS' BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Common image file extensions (case-insensitive)
 */
const IMAGE_EXTENSIONS = [
    '.jpg',
    '.jpeg',
    '.png',
    '.webp',
    '.gif',
    '.bmp',
    '.svg',
];
/**
 * Common video file extensions (case-insensitive)
 */
const VIDEO_EXTENSIONS = [
    '.mp4',
    '.webm',
    '.ogg',
    '.ogv',
    '.mov',
    '.m4v',
];
/**
 * Common audio file extensions (case-insensitive)
 */
const AUDIO_EXTENSIONS = [
    '.mp3',
    '.wav',
    '.flac',
    '.aac',
    '.m4a',
    '.ogg',
    '.oga',
    '.opus',
];
/**
 * Common PDF file extensions (case-insensitive)
 */
const PDF_EXTENSIONS = [
    '.pdf',
];
/**
 * Checks if a URL points to an image file by examining its extension.
 *
 * @param url The URL to check
 * @returns true if the URL appears to be an image, false otherwise
 */
export const isImageUrl = (url) => {
    if (!url) {
        return false;
    }
    try {
        // Extract the pathname from the URL to get the file extension
        const urlObj = new URL(url, window.location.toString());
        const pathname = urlObj.pathname.toLowerCase();
        // Check if the pathname ends with any image extension
        return IMAGE_EXTENSIONS.some((ext) => pathname.endsWith(ext));
    }
    catch (error) {
        // If URL parsing fails, try a simple string check
        const urlLower = url.toLowerCase();
        return IMAGE_EXTENSIONS.some((ext) => urlLower.endsWith(ext));
    }
};
/**
 * Checks if a URL points to a video file by examining its extension.
 *
 * @param url The URL to check
 * @returns true if the URL appears to be a video, false otherwise
 */
export const isVideoUrl = (url) => {
    if (!url) {
        return false;
    }
    try {
        // Extract the pathname from the URL to get the file extension
        const urlObj = new URL(url, window.location.toString());
        const pathname = urlObj.pathname.toLowerCase();
        // Check if the pathname ends with any video extension
        return VIDEO_EXTENSIONS.some((ext) => pathname.endsWith(ext));
    }
    catch (error) {
        // If URL parsing fails, try a simple string check
        const urlLower = url.toLowerCase();
        return VIDEO_EXTENSIONS.some((ext) => urlLower.endsWith(ext));
    }
};
/**
 * Checks if a URL points to an audio file by examining its extension.
 *
 * @param url The URL to check
 * @returns true if the URL appears to be an audio file, false otherwise
 */
export const isAudioUrl = (url) => {
    if (!url) {
        return false;
    }
    try {
        // Extract the pathname from the URL to get the file extension
        const urlObj = new URL(url, window.location.toString());
        const pathname = urlObj.pathname.toLowerCase();
        // Check if the pathname ends with any audio extension
        return AUDIO_EXTENSIONS.some((ext) => pathname.endsWith(ext));
    }
    catch (error) {
        // If URL parsing fails, try a simple string check
        const urlLower = url.toLowerCase();
        return AUDIO_EXTENSIONS.some((ext) => urlLower.endsWith(ext));
    }
};
/**
 * Checks if a URL points to a PDF file by examining its extension.
 *
 * @param url The URL to check
 * @returns true if the URL appears to be a PDF file, false otherwise
 */
export const isPdfUrl = (url) => {
    if (!url) {
        return false;
    }
    try {
        // Extract the pathname from the URL to get the file extension
        const urlObj = new URL(url, window.location.toString());
        const pathname = urlObj.pathname.toLowerCase();
        // Check if the pathname ends with any PDF extension
        return PDF_EXTENSIONS.some((ext) => pathname.endsWith(ext));
    }
    catch (error) {
        // If URL parsing fails, try a simple string check
        const urlLower = url.toLowerCase();
        return PDF_EXTENSIONS.some((ext) => urlLower.endsWith(ext));
    }
};
//# sourceMappingURL=content-type-detector.js.map